import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { BottomSheet } from "./ui/bottom-sheet";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { MapPin, Phone, Check, Truck } from "lucide-react";

export default function AssignDriverDialog({ orderId, orderStatus, open, onOpenChange, onAssigned }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  // Estado clave compuesto driver_id + acción para que el spinner se
  // muestre sólo en el botón presionado (Asignar vs Asignar+camino).
  const [assigning, setAssigning] = useState(null);

  const canDispatch = orderStatus === "confirmed";

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    api.get(`/drivers/nearest/${orderId}`).then((r) => setDrivers(r.data)).finally(() => setLoading(false));
  }, [open, orderId]);

  const assign = async (driver_id, markOnTheWay) => {
    const key = `${driver_id}:${markOnTheWay ? "dispatch" : "assign"}`;
    setAssigning(key);
    try {
      await api.patch(`/orders/${orderId}/assign`, {
        driver_id,
        mark_on_the_way: !!markOnTheWay,
      });
      toast.success(markOnTheWay ? "Unidad asignada y pedido en camino" : "Unidad de reparto asignada");
      onAssigned?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAssigning(null);
    }
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Asignar unidad de reparto"
      description="Ordenados por cercanía a la dirección de entrega."
      testid="assign-driver-sheet"
    >
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {loading && <p className="text-sm text-neutral-500">Cargando...</p>}
          {!loading && drivers.length === 0 && (
            <p className="text-sm text-neutral-500">No hay unidades de reparto disponibles. Agrégalas en el panel de Unidades de Reparto.</p>
          )}
          {drivers.map((d) => {
            const assignKey = `${d.driver_id}:assign`;
            const dispatchKey = `${d.driver_id}:dispatch`;
            const isAssigning = assigning === assignKey;
            const isDispatching = assigning === dispatchKey;
            const anyBusy = !!assigning;
            return (
              <div
                key={d.driver_id}
                className="rounded-xl border border-neutral-200 p-4"
                data-testid={`assign-driver-row-${d.driver_id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-neutral-100 border border-neutral-200 overflow-hidden shrink-0 flex items-center justify-center">
                      {d.image_url ? (
                        <img src={d.image_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold text-neutral-600">{d.name?.[0]?.toUpperCase()}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{d.name}</p>
                      <p className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        {d.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {d.phone}</span>}
                        {d.vehicle_plate && <span>• {d.vehicle_plate}</span>}
                        {typeof d.orders_today === "number" && (
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                              d.orders_today === 0 ? "bg-neutral-100 text-neutral-600" :
                              d.orders_today <= 3 ? "bg-emerald-50 text-emerald-700" :
                              d.orders_today <= 6 ? "bg-amber-50 text-amber-700" :
                              "bg-red-50 text-red-700"
                            }`}
                            title="Pedidos asignados con entrega para hoy"
                            data-testid={`driver-orders-today-${d.driver_id}`}
                          >
                            Entregas hoy: {d.orders_today}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="flex items-center gap-1 text-xs font-semibold text-neutral-700 justify-end">
                      <MapPin size={12} />
                      {d.distance_km != null ? `${d.distance_km} km` : "—"}
                    </p>
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 mt-1 inline-block ${
                      d.status === "available" ? "bg-green-50 text-green-700" :
                      d.status === "busy" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-500"
                    }`}>
                      {d.status === "available" ? "Disponible" : d.status === "busy" ? "Ocupado" : "Offline"}
                    </span>
                  </div>
                </div>
                {/* Acciones por unidad: Asignar (sólo asigna) vs Asignar
                    y poner en camino (asigna + transición a on_the_way).
                    El segundo botón sólo se habilita en estado "confirmed",
                    porque desde "pending" haría falta confirmar antes y
                    queremos que el operador lo haga explícito. */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    variant="outline"
                    className="h-10 justify-center text-sm"
                    onClick={() => assign(d.driver_id, false)}
                    disabled={anyBusy}
                    data-testid={`assign-driver-${d.driver_id}`}
                  >
                    {isAssigning ? (
                      <><Check size={12} className="mr-1.5 animate-pulse" /> Asignando…</>
                    ) : (
                      "Asignar"
                    )}
                  </Button>
                  <Button
                    className="h-10 justify-center text-sm font-semibold"
                    style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                    onClick={() => assign(d.driver_id, true)}
                    disabled={!canDispatch || anyBusy}
                    title={!canDispatch ? "Sólo disponible cuando el pedido está confirmado" : undefined}
                    data-testid={`dispatch-driver-${d.driver_id}`}
                  >
                    {isDispatching ? (
                      <><Truck size={12} className="mr-1.5 animate-pulse" /> Enviando…</>
                    ) : (
                      <><Truck size={13} className="mr-1.5" /> Asignar y poner en camino</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        {!canDispatch && drivers.length > 0 && (
          <p className="text-[11px] text-neutral-500 mt-2 leading-snug" data-testid="dispatch-hint">
            "Asignar y poner en camino" se habilita cuando el pedido está confirmado.
          </p>
        )}
        <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full mt-3 tap-fade" data-testid="close-assign-dialog">Cancelar</Button>
      </div>
    </BottomSheet>
  );
}
